import { lazy } from 'react';
import { useSelector } from 'react-redux';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect
} from 'react-router-dom';

import { districtSelectors, globalSelectors } from '../../store/selectors';

import LocationListener from '../../components/LocationListener';
import LanguageRecognizer from '../../components/LanguageRecognizer';
import LogoutCountdownTimer from '../../components/LogoutCountdownTimer';
import SuspenseWrapper from '../../components/SuspenseWrapper';
import ComponentsReadyListener from '@/components/ComponentsReadyListener';

import mainRoutePaths from './paths';
import isItIframe from '../../utils/isItIframe';

const HeaderPortal = lazy(() => import('../../components/HeaderPortal'));
const HomeView = lazy(() => import('../../views/Home'));
const LoginView = lazy(() => import('../../views/Login'));
const RegistrationView = lazy(() => import('../../views/Registration'));
const ConfirmEmailView = lazy(() => import('../../views/ConfirmEmail'));
const UserProfileView = lazy(() => import('../../views/UserProfileView'));
const MainAdminPanelView = lazy(() => import('../../views/MainAdminPanel'));
const ResetPasswordView = lazy(() => import('../../views/ResetPassword'));
const ConfirmEmailFromAdminView = lazy(() =>
  import('../../views/ConfirmEmailFromAdmin')
);
const SetNewPasswordView = lazy(() => import('../../views/SetNewPassword'));
const ActivateChangedEmailView = lazy(() =>
  import('../../views/ActivateChangedEmail')
);
const Page404 = lazy(() => import('../../views/Page404'));
const DistrictRouter = lazy(() => import('../../router/district'));
const ForbiddenView = lazy(() => import('../../views/ForbiddenView'));
const PopUpDetailsView = lazy(() => import('../../views/PopUpDetailsView'));
const MaintenancePage = lazy(() => import('../../views/MaintenancePage'));
const Regulations = lazy(() => import('../../views/Regulations'));

const { getDistricts } = districtSelectors;
const { getGlobalSettings, getIsMapPortalLoading } = globalSelectors;

const PortalRouter = () => {
  const districts = useSelector(getDistricts);
  const globalSettings = useSelector(getGlobalSettings);
  const mapPortalLoader = useSelector(getIsMapPortalLoading);

  const districtRegex = districts.join('|');

  const isDistrictListHomePage = globalSettings.get(
    'home_page_is_districts_list'
  );

  const {
    districtList,
    homePage,
    login,
    registration,
    activation,
    adminActivation,
    profile,
    admin,
    availability_statement,
    terms,
    privacy,
    resetPassword,
    setNewPassword,
    activateChangedEmail,
    forbidden,
    notFound,
    maintenancePage
  } = mainRoutePaths;

  return (
    <Router>
      <LocationListener />
      {!isItIframe() && (
        <SuspenseWrapper ignoreFallback>
          <HeaderPortal />
        </SuspenseWrapper>
      )}
      <LanguageRecognizer />
      <Switch>
        <Route
          exact
          path={homePage}
          render={() =>
            isDistrictListHomePage ? (
              <Redirect to={districtList} />
            ) : (
              <SuspenseWrapper withFooter>
                <HomeView />
              </SuspenseWrapper>
            )
          }
        />
        <Route
          exact
          path={districtList}
          render={() =>
            isDistrictListHomePage ? (
              <SuspenseWrapper withFooter>
                <HomeView />
              </SuspenseWrapper>
            ) : (
              <Redirect to={''} />
            )
          }
        />
        <Route
          path={login}
          render={() => (
            <SuspenseWrapper withFooter>
              <LoginView />
            </SuspenseWrapper>
          )}
        />
        <Route
          path={registration}
          render={() => (
            <SuspenseWrapper withFooter>
              <RegistrationView />
            </SuspenseWrapper>
          )}
        />
        <Route
          path={activation}
          render={() => (
            <SuspenseWrapper withFooter>
              <ConfirmEmailView />
            </SuspenseWrapper>
          )}
        />
        <Route
          path={adminActivation}
          render={() => (
            <SuspenseWrapper withFooter>
              <ConfirmEmailFromAdminView />
            </SuspenseWrapper>
          )}
        />
        <Route
          path={profile}
          render={() => (
            <SuspenseWrapper>
              <UserProfileView />
            </SuspenseWrapper>
          )}
        />
        <Route
          path={admin}
          render={() => (
            <SuspenseWrapper>
              <MainAdminPanelView />
            </SuspenseWrapper>
          )}
        />
        <Route
          path={availability_statement}
          render={() => (
            <SuspenseWrapper withFooter>
              <Regulations regulations={availability_statement} />
            </SuspenseWrapper>
          )}
        />
        <Route
          path={terms}
          render={() => (
            <SuspenseWrapper withFooter>
              <Regulations regulations={terms} />
            </SuspenseWrapper>
          )}
        />
        <Route
          path={privacy}
          render={() => (
            <SuspenseWrapper withFooter>
              <Regulations regulations={privacy} />
            </SuspenseWrapper>
          )}
        />
        <Route
          exact
          path={resetPassword}
          render={() => (
            <SuspenseWrapper>
              <ResetPasswordView withFooter />
            </SuspenseWrapper>
          )}
        />
        <Route
          path={setNewPassword}
          render={() => (
            <SuspenseWrapper>
              <SetNewPasswordView withFooter />
            </SuspenseWrapper>
          )}
        />
        <Route
          path={activateChangedEmail}
          render={() => (
            <SuspenseWrapper>
              <ActivateChangedEmailView withFooter />
            </SuspenseWrapper>
          )}
        />
        <Route
          path={`/:prefix(${districtRegex})`}
          render={() => (
            <SuspenseWrapper>
              <DistrictRouter />
            </SuspenseWrapper>
          )}
        />
        <Route
          path={forbidden}
          render={() => (
            <SuspenseWrapper>
              <ForbiddenView />
            </SuspenseWrapper>
          )}
        />
        <Route
          exact
          path={'/popup_details'}
          render={() => (
            <SuspenseWrapper>
              <PopUpDetailsView />
            </SuspenseWrapper>
          )}
        />
        <Route
          path={notFound}
          render={() => (
            <SuspenseWrapper>
              <Page404 />
            </SuspenseWrapper>
          )}
        />
        <Route
          path={maintenancePage}
          render={props => (
            <SuspenseWrapper>
              <MaintenancePage {...props} />
            </SuspenseWrapper>
          )}
        />
        <Route
          component={() => (
            <Redirect
              to={{
                pathname: notFound
              }}
            />
          )}
        />
      </Switch>
      <LogoutCountdownTimer />
      {mapPortalLoader && <ComponentsReadyListener />}
    </Router>
  );
};

export default PortalRouter;
